import React, { useEffect, useRef, useState } from 'react';
import {
  getAskYourResultsPrompts,
  updateAskYourResultsPrompts,
  getGpSnapshotPrompts,
  updateGpSnapshotPrompts,
  getRestrictedChatPrompts,
  updateRestrictedChatPrompts,
  getPersonalisedMonitoringPlanPrompts,
  updatePersonalisedMonitoringPlanPrompts,
} from '../../../api/chat';
import { toast } from 'react-toastify';
import { ReactComponent as UploadIcon } from '../../../assets/icons/download.svg';
import TextArea from '../../BaseComponents/TextArea';
import Button from '../../BaseComponents/Button';
import BodySystemAnalysisPromptPanel from './BodySystemAnalysisPromptPanel';
import './PromptsTab.scss';

type PromptTab =
  | 'ask_your_results'
  | 'gp_snapshot'
  | 'restricted_chat'
  | 'personalised_monitoring_plan'
  | 'body_system_analysis';

type PromptFormTab = Exclude<PromptTab, 'body_system_analysis'>;

interface PromptFormState {
  rulesAndGuidelines: string;
  actualPrompt: string;
  contextAttachment: string;
  attachmentFileName: string;
}

const EMPTY_FORM: PromptFormState = {
  rulesAndGuidelines: '',
  actualPrompt: '',
  contextAttachment: '',
  attachmentFileName: '',
};

const PROMPT_TABS: { id: PromptTab; label: string }[] = [
  { id: 'ask_your_results', label: 'Ask Your Results' },
  { id: 'gp_snapshot', label: 'GP Snapshot' },
  { id: 'restricted_chat', label: 'Unrestricted Chat' },
  { id: 'personalised_monitoring_plan', label: 'Personalised monitoring plan' },
  { id: 'body_system_analysis', label: 'Body System Analysis Prompt' },
];

const readField = (data: Record<string, unknown>, snake: string, camel: string): string => {
  const snakeValue = data[snake];
  if (typeof snakeValue === 'string') return snakeValue;
  const camelValue = data[camel];
  if (typeof camelValue === 'string') return camelValue;
  return '';
};

const savedAttachmentLabel = (content: string): string => {
  if (!content.trim()) return '';
  const firstLine = content
    .split('\n')
    .map((line) => line.trim())
    .find(Boolean);
  return firstLine ? `Saved attachment — ${firstLine}` : 'Saved attachment (.txt)';
};

const PromptsTab = () => {
  const [activeTab, setActiveTab] = useState<PromptTab>('ask_your_results');
  const [forms, setForms] = useState<Record<PromptFormTab, PromptFormState>>({
    ask_your_results: { ...EMPTY_FORM },
    gp_snapshot: { ...EMPTY_FORM },
    restricted_chat: { ...EMPTY_FORM },
    personalised_monitoring_plan: { ...EMPTY_FORM },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    setIsLoading(true);
    try {
      const [askData, gpData, restrictedData, monitoringPlanData] = await Promise.all([
        getAskYourResultsPrompts(),
        getGpSnapshotPrompts(),
        getRestrictedChatPrompts(),
        getPersonalisedMonitoringPlanPrompts(),
      ]);

      const askAttachment = readField(askData, 'context_attachment', 'contextAttachment');
      const gpAttachment = readField(gpData, 'context_attachment', 'contextAttachment');
      const restrictedAttachment = readField(
        restrictedData,
        'context_attachment',
        'contextAttachment'
      );
      const monitoringPlanAttachment = readField(
        monitoringPlanData,
        'context_attachment',
        'contextAttachment'
      );

      setForms({
        ask_your_results: {
          rulesAndGuidelines: readField(askData, 'rules_and_guidelines', 'rulesAndGuidelines'),
          actualPrompt: '',
          contextAttachment: askAttachment,
          attachmentFileName: savedAttachmentLabel(askAttachment),
        },
        gp_snapshot: {
          rulesAndGuidelines: readField(gpData, 'rules_and_guidelines', 'rulesAndGuidelines'),
          actualPrompt: readField(gpData, 'actual_prompt', 'actualPrompt'),
          contextAttachment: gpAttachment,
          attachmentFileName: savedAttachmentLabel(gpAttachment),
        },
        restricted_chat: {
          rulesAndGuidelines: readField(restrictedData, 'rules_and_guidelines', 'rulesAndGuidelines'),
          actualPrompt: '',
          contextAttachment: restrictedAttachment,
          attachmentFileName: savedAttachmentLabel(restrictedAttachment),
        },
        personalised_monitoring_plan: {
          rulesAndGuidelines: readField(
            monitoringPlanData,
            'rules_and_guidelines',
            'rulesAndGuidelines'
          ),
          actualPrompt: readField(monitoringPlanData, 'actual_prompt', 'actualPrompt'),
          contextAttachment: monitoringPlanAttachment,
          attachmentFileName: savedAttachmentLabel(monitoringPlanAttachment),
        },
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to load prompts. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateForm = (tab: PromptFormTab, patch: Partial<PromptFormState>) => {
    setForms((current) => ({
      ...current,
      [tab]: {
        ...current[tab],
        ...patch,
      },
    }));
  };

  const handleAttachmentSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (activeTab === 'body_system_analysis') return;

    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.txt')) {
      toast.error('Only .txt files are supported.');
      return;
    }

    try {
      const content = await file.text();
      updateForm(activeTab, {
        contextAttachment: content,
        attachmentFileName: file.name,
      });
    } catch {
      toast.error('Failed to read the selected file.');
    }
  };

  const handleRemoveAttachment = async () => {
    if (isSaving || activeTab === 'body_system_analysis') return;

    setIsSaving(true);
    try {
      if (activeTab === 'ask_your_results') {
        await updateAskYourResultsPrompts({ context_attachment: '' });
      } else if (activeTab === 'gp_snapshot') {
        await updateGpSnapshotPrompts({ context_attachment: '' });
      } else if (activeTab === 'personalised_monitoring_plan') {
        await updatePersonalisedMonitoringPlanPrompts({ context_attachment: '' });
      } else {
        await updateRestrictedChatPrompts({ context_attachment: '' });
      }

      updateForm(activeTab, {
        contextAttachment: '',
        attachmentFileName: '',
      });
      toast.success('Attachment removed');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to remove attachment. Please try again.';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (isSaving || activeTab === 'body_system_analysis') return;

    const currentForm = forms[activeTab];
    setIsSaving(true);
    try {
      if (activeTab === 'ask_your_results') {
        await updateAskYourResultsPrompts({
          rules_and_guidelines: currentForm.rulesAndGuidelines,
          context_attachment: currentForm.contextAttachment,
        });
      } else if (activeTab === 'gp_snapshot') {
        await updateGpSnapshotPrompts({
          rules_and_guidelines: currentForm.rulesAndGuidelines,
          actual_prompt: currentForm.actualPrompt,
          context_attachment: currentForm.contextAttachment,
        });
      } else if (activeTab === 'personalised_monitoring_plan') {
        await updatePersonalisedMonitoringPlanPrompts({
          rules_and_guidelines: currentForm.rulesAndGuidelines,
          actual_prompt: currentForm.actualPrompt,
          context_attachment: currentForm.contextAttachment,
        });
      } else {
        await updateRestrictedChatPrompts({
          rules_and_guidelines: currentForm.rulesAndGuidelines,
          context_attachment: currentForm.contextAttachment,
        });
      }

      toast.success('Prompt settings saved successfully');
      fetchPrompts();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save prompt settings. Please try again.';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const currentForm = activeTab === 'body_system_analysis' ? null : forms[activeTab];

  return (
    <div className="prompts-tab-container">
      <div className="prompts-sub-tabs-container">
        {PROMPT_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`prompts-sub-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'body_system_analysis' ? (
        <BodySystemAnalysisPromptPanel />
      ) : isLoading ? (
        <div className="prompts-loading">Loading prompt settings...</div>
      ) : (
        <div className="prompts-form">
          <section className="prompt-field-card">
            <TextArea
              title="System Rules & Guidelines"
              name={`${activeTab}_rules_and_guidelines`}
              value={currentForm!.rulesAndGuidelines}
              placeholder="Enter system rules and guidelines..."
              rows={14}
              isDisabled={isSaving}
              onChange={(event) =>
                updateForm(activeTab, { rulesAndGuidelines: event.target.value })
              }
            />
          </section>

          {(activeTab === 'gp_snapshot' || activeTab === 'personalised_monitoring_plan') && (
            <section className="prompt-field-card">
              <TextArea
                title="Actual Prompt"
                name={`${activeTab}_actual_prompt`}
                value={currentForm!.actualPrompt}
                placeholder={
                  activeTab === 'gp_snapshot'
                    ? 'Enter the GP snapshot generation prompt...'
                    : 'Enter the personalised monitoring plan generation prompt...'
                }
                rows={14}
                isDisabled={isSaving}
                onChange={(event) =>
                  updateForm(activeTab, { actualPrompt: event.target.value })
                }
              />
            </section>
          )}

          <section className="prompt-field-card prompt-attachment-section">
            <div className="prompt-attachment-header">
              <h3 className="prompt-attachment-title">Attachment (.txt)</h3>
              <p className="prompt-attachment-help">
                Upload a single .txt file, then click Save. Remove clears the attachment immediately.
              </p>
            </div>

            <div className="prompt-attachment-dropzone">
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,text/plain"
                className="prompt-attachment-input"
                onChange={handleAttachmentSelect}
              />

              {currentForm!.attachmentFileName ? (
                <div className="prompt-attachment-meta">
                  <div className="prompt-attachment-file">
                    <span className="prompt-attachment-filename">
                      {currentForm!.attachmentFileName}
                    </span>
                    <span className="prompt-attachment-size">
                      {currentForm!.contextAttachment.length.toLocaleString()} characters
                    </span>
                  </div>
                  <div className="prompt-attachment-actions">
                    <button
                      type="button"
                      className="prompt-attachment-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSaving}
                    >
                      Replace file
                    </button>
                    <button
                      type="button"
                      className="prompt-attachment-button prompt-attachment-button--danger"
                      onClick={handleRemoveAttachment}
                      disabled={isSaving}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="prompt-attachment-empty-state">
                  <UploadIcon className="prompt-attachment-icon" aria-hidden />
                  <p className="prompt-attachment-empty-text">No attachment uploaded.</p>
                  <button
                    type="button"
                    className="prompt-attachment-button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSaving}
                  >
                    Upload .txt
                  </button>
                </div>
              )}
            </div>
          </section>

          <div className="prompt-form-actions">
            <Button
              text={isSaving ? 'Saving…' : 'Save'}
              onClick={handleSave}
              isDisabled={isSaving}
              width="140px"
              className="prompt-save-button"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptsTab;
